import { BasiqActions } from "./basiq.actions";
import { Origin, SyncStatusEnum } from "../../../../models/types/general-types";
import { BasiqAccountType } from "../../../../models/types/basiq.types";
import { AccountView } from "../../../../models/view/account/account.view";
import { SourceAccountView } from "../../../../models/view/source-book/source-account.view";
import { AccountStoreModel } from "../../../../models/store/account.store.model";
import { SourceAccountStoreModel } from "../../../../models/store/source-account-store.model";
import { InstitutionView } from "../../../../models/view/institution/institution.view";
import { InstitutionAccountView } from "../../../../models/view/institution/institution-account.view";
import { BasiqTypes } from "../../../../models/types/account.types";
import { AccountTypes } from "../../../../models/enum/account.enum";
import { Injectable } from "@angular/core";
import { isCreditType } from "@bitwarden/web-vault/app/shared/utils/helper.account/account-type";

@Injectable({
  providedIn: "root",
})
export class BasiqAccountAction extends BasiqActions {
  async saveAccounts() {
    try {
      this.appState.startProcess("importing-data", "Saving your accounts");
      const basiqAccountResponse = await this.basiqDataApi.accounts();
      if (!basiqAccountResponse) {
        this.appState.endProcess("importing-data");
        return null;
      }

      // Org-owned accounts are excluded from the match candidates: a personal account shared/forked
      // into an org (`ManageAccountsComponent.shareAccountToOrg`) keeps the same provider external
      // id (`bsqId`) as the original, so an unfiltered store-wide search here could resolve to the
      // org fork instead of the personal original. Not exploitable today only because the fork
      // never creates a `SourceAccountView` row for the org copy — defensive filter in case that
      // changes.
      const existingBasiqAccounts = this.userStore.enrichAccounts
        .collection()
        ?.filter((account) => account.origin === Origin.basiq && !account.isOrganizationOwned?.());

      const existingBasiqSourceAccounts = this.importStore.sourceAccount
        .sourceBookViews()
        ?.filter((source) => source.origin === Origin.basiq);
      const newAccounts: BasiqAccountType[] = [];
      for (const basiqAccount of basiqAccountResponse.accounts) {
        const existingSourceAccounts = existingBasiqSourceAccounts?.filter(
          (existingAccount) => existingAccount.source === basiqAccount.name,
        );
        let existingAccount = null;

        if (existingSourceAccounts && existingSourceAccounts.length > 0) {
          existingAccount = existingBasiqAccounts?.find((existingBook) =>
            existingSourceAccounts.find((sa) => sa.accountId === existingBook.id),
          );
        }

        if (!existingAccount) {
          newAccounts.push(basiqAccount);
        }
      }

      if (newAccounts.length > 0) {
        this.appState.endProcess("importing-data");
        return await this.saveNewAccounts(newAccounts);
      }

      this.appState.endProcess("importing-data");
      return [];
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "Savings account from basiq",
        SyncStatusEnum.unknownError,
      );
      this.appState.endProcess("importing-data");
      return [];
    }
  }

  async saveNewAccounts(newBasiqBasedAccounts: BasiqAccountType[]): Promise<AccountView[]> {
    try {
      const newAccounts: AccountView[] = [];
      const newSourceAccounts: SourceAccountView[] = [];

      for (const basiqAccount of newBasiqBasedAccounts) {
        this.appState.startProcess("importing-data", `Creating ${basiqAccount.name}`);
        const accountStoreModel = await this.toAccountStoreModel(basiqAccount);
        const sourceAccountStoreModel = this.toSourceAccountStoreModel(
          basiqAccount,
          accountStoreModel,
        );

        newSourceAccounts.push(new SourceAccountView(sourceAccountStoreModel));
        const accountView = new AccountView(accountStoreModel);
        /** @Sinan At this stage I need to have the Basiq balance on the account
         * Is it a good idea to set the c_balance tot he value ? it is just for calculating the opening balance
         * */
        accountView.c_balance = isCreditType(basiqAccount.class.type)
          ? Number(basiqAccount.balance)
          : Number(basiqAccount.availableFunds);
        newAccounts.push(accountView);
        this.appState.endProcess("importing-data");
      }

      this.appState.startProcess("importing-data", `Saving  created accounts`);
      await this.importStore.sourceAccount.saveToVault(newSourceAccounts, true);
      await this.userStore.accounts.saveToVault(newAccounts, true);
      this.appState.endProcess("importing-data");

      /** Return the accounts from the store, so they have vault-id...no duplicates anymore
       * add the c_balance from above for calculating balance correctly moving forward...
       * */

      return this.userStore.accounts.accountViews().reduce((acc, view) => {
        const newAccount = newAccounts.find((account) => account.id === view.id);
        if (newAccount) {
          const clone = view.clone();
          clone.c_balance = newAccount.c_balance;
          acc.push(clone);
        }
        return acc;
      }, []);
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "creating & saving accounts",
        SyncStatusEnum.unknownError,
      );
      return [];
    }
  }

  toSourceAccountStoreModel(
    basiqAccount: BasiqAccountType,
    accountStoreModel: AccountStoreModel,
  ): SourceAccountStoreModel {
    return {
      src: basiqAccount.name,
      acId: accountStoreModel.id,
      org: Origin.basiq,
      v: 1,
      vid: "",
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      id: crypto.randomUUID(),
    };
  }

  async toAccountStoreModel(basiqAccount: BasiqAccountType): Promise<AccountStoreModel> {
    const basiqBasedInstitutions = this.userStore.institutions
      .institutionViews()
      .filter((inst) => !!inst.instoBasiqId);
    const institution = basiqBasedInstitutions.find(
      (basiqBasedInsto) => basiqBasedInsto.instoBasiqId === basiqAccount.institution,
    );

    const accountType = await this.getAccountProduct(basiqAccount, institution);
    return {
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      id: crypto.randomUUID(),
      v: 3,
      rtInstLnk: null,
      vid: "",
      instLnk: {
        institutionId: institution?.id,
        institutionAccountId: accountType?.id,
      },
      curr: basiqAccount.currency,
      name: basiqAccount.name,
      crLm: Number(basiqAccount.creditLimit),
      bal: 0,
      tz: "Australia/Sydney",
      dfltCla: [],
      dfltCat: [],
      ori: Origin.basiq,
      bsqId: basiqAccount.id,
      date: new Date().toISOString(),
      type: basiqAccount.class.type,
      st: [],
    };
  }

  private async getAccountProduct(basiqAccount: BasiqAccountType, institution: InstitutionView) {
    let accountProduct: InstitutionAccountView = null;
    if (institution?.availableAccounts?.length) {
      accountProduct = institution.availableAccounts.find((accountType) => {
        return accountType.name.toLowerCase() === basiqAccount.class.product.toLowerCase();
      });
    }

    if (!accountProduct) {
      accountProduct = await this.generateAccountProduct(basiqAccount, institution);
    }

    return accountProduct;
  }

  private async generateAccountProduct(
    basiqAccount: BasiqAccountType,
    institution: InstitutionView,
  ): Promise<InstitutionAccountView> {
    const i = institution.clone();
    const newAccountType: InstitutionAccountView = {
      name: basiqAccount.class.product,
      interestRates: [],
      id: crypto.randomUUID(),
    };
    if (i.availableAccounts) {
      i.availableAccounts.push(newAccountType);
    } else {
      i.availableAccounts = [newAccountType];
    }
    await this.userStore.institutions.save(i);
    return newAccountType;
  }

  isCreditType(type: BasiqTypes): boolean {
    return (
      type === AccountTypes.creditCard ||
      type === AccountTypes.mortgage ||
      type === AccountTypes.loan
    );
  }
}
